import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Category, useCategories } from '@/hooks/use-categories';
import { Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const AVAILABLE_ICONS = [
  'CircleDot', 'Wallet', 'Home', 'Car', 'ShoppingCart', 'Utensils',
  'Heart', 'Briefcase', 'GraduationCap', 'Plane', 'Gift', 'Music',
  'Coffee', 'Pizza', 'Fuel', 'Smartphone', 'Tv', 'Shirt',
  'Pill', 'Dumbbell', 'Dog', 'Baby', 'Scissors', 'Wrench',
  'Building', 'CreditCard', 'Banknote', 'PiggyBank', 'TrendingUp', 'TrendingDown',
];

const AVAILABLE_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#78716c',
];

interface CategoryEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
  onSuccess: () => void;
}

export function CategoryEditModal({
  open,
  onOpenChange,
  category,
  onSuccess
}: CategoryEditModalProps) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('CircleDot');
  const [color, setColor] = useState('#3b82f6');
  const [parentId, setParentId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { categories } = useCategories(category?.type || 'expense');

  // Filter out self and children to prevent circular references
  const availableParents = categories.filter(c => 
    category && c.id !== category.id && 
    c.parent_id !== category.id && 
    !c.parent_id // Only show top-level categories as parents
  );

  useEffect(() => {
    if (category) {
      setName(category.name);
      setIcon(category.icon);
      setColor(category.color);
      setParentId(category.parent_id);
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !category) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('categories')
        .update({
          name: name.trim(),
          icon,
          color,
          parent_id: parentId || null
        })
        .eq('id', category.id);

      if (error) throw error;

      toast({
        title: 'Categoria atualizada!',
        description: `${name} foi atualizada com sucesso.`
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar',
        description: error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const renderIcon = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Categoria</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Alimentação"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Categoria Pai (opcional)</Label>
            <Select
              value={parentId || 'none'}
              onValueChange={(val) => setParentId(val === 'none' ? null : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Nenhuma (categoria principal)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma (categoria principal)</SelectItem>
                {availableParents.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Ícone</Label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 p-2 border rounded-md max-h-32 overflow-y-auto">
              {AVAILABLE_ICONS.map((iconName) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => setIcon(iconName)}
                  className={`p-2 rounded-md hover:bg-accent flex items-center justify-center transition-colors ${
                    icon === iconName ? 'bg-primary text-primary-foreground' : ''
                  }`}
                >
                  {renderIcon(iconName)}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="grid grid-cols-6 sm:grid-cols-9 gap-2 p-2 border rounded-md">
              {AVAILABLE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-6 w-6 rounded-full transition-transform ${
                    color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving || !name.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
